// Rendu du récapitulatif de commande de l'interface actuelle. Toute la
// logique vit dans useCheckoutForm.tsx : ce fichier ne contient plus que
// l'affichage historique, inchangé.
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Spacer,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { Controller, FormProvider } from 'react-hook-form';
import InputMask from 'react-input-mask';

import { CheckoutGiftCard } from './components/CheckoutGiftCard';
import { CheckoutPromoCode } from './components/CheckoutPromoCode';
import { ContractAgreementCheckbox } from './components/ContractAgreementCheckbox';
import { SavedCardExpirationAlert } from './components/SavedCardExpirationAlert';
import { APPLE_PAY_METHOD, CheckoutFormProps, GOOGLE_PAY_METHOD, PaymentMethod, useCheckoutForm, WALLET_METHODS } from './useCheckoutForm';
import { WalletExpress, WalletId } from 'components/WalletExpress';
import { WalletPaymentOverlay } from 'components/WalletPaymentOverlay';
import { CheckoutFormV2 } from './v2/CheckoutFormV2';
import { isNewCheckoutEnabled } from './v2/flag';

export type {
  CheckoutData,
  CheckoutFormProps,
  CheckoutSuccess,
  CheckoutVariant,
  ContractAgreementProps,
  PaymentMethod,
} from './useCheckoutForm';

/**
 * Point d'entrée du formulaire de paiement. Aiguille vers le Nouveau Panier
 * (panier de la nouvelle interface, cf. v2/flag.ts) ou vers le rendu
 * historique. Les deux consomment le même hook `useCheckoutForm` : aucune
 * divergence possible sur la logique de paiement.
 */
export function CheckoutForm(props: CheckoutFormProps) {
  if (isNewCheckoutEnabled()) {
    return <CheckoutFormV2 {...props} />;
  }
  return <CheckoutFormLegacy {...props} />;
}

function CheckoutFormLegacy(props: CheckoutFormProps) {
  const {
    form,
    register,
    control,
    errors,
    isSubmitting,
    handleSubmit,
    onSubmit,
    product,
    productAmount,
    totalAmount,
    remainingAmount,
    vatAmount,
    discount,
    giftCardValue,
    giftCardValueCoversTotalAmount,
    getProductPrice,
    clientCompleteInfo,
    clientCreditCard,
    clientHasStoredCreditCard,
    storedCardExpiresBeforeEngagement,
    isUsingSavedCreditCard,
    storedCreditCard,
    creditCardNumber,
    isAmex,
    paymentMethod,
    handlePaymentChange,
    customPaymentMethods,
    isApplePayAvailable,
    isGooglePayAvailable,
    walletStep,
    isAlmaAvailable,
    isAlmaSelectable,
    isLoadingPaymentPlans,
    isRefetchingPaymentPlans,
    updatedPaymentPlans,
    selectedPlan,
    handlePlanSelect,
    paymentInProgress,
    almaRedirectInProgress,
    showFallbackRedirect,
    handleFallbackRedirect,
    submitEnabled,
    disableConfirmAlmaPayment,
    getDisabledTooltipMessage,
    isContract,
    contractAgreement,
    isLoggedIn,
    navigate,
    t,
  } = useCheckoutForm(props);

  if (!isLoggedIn) {

    // Naviguer vers la page de login avec l'URL actuelle comme onLoginRedirect
    const currentPath = location.pathname + location.search + location.hash;

    navigate('/account', {
      state: {
        onLoginRedirect: currentPath,
      },
      replace: true,
    });
    return null;
  }

  // Si aucun produit n'est disponible, afficher un message d'erreur
  if (!product) {
    return (
      <Box p={4} textAlign="center">
        <Text>Erreur : Aucun produit sélectionné</Text>
      </Box>
    );
  }

  return (
    <FormProvider {...form}>
      <Stack spacing={5} direction={{ base: 'column', md: 'row' }} className="checkoutForm">
        <Table borderRadius="5px" width={{ base: '100%', md: 'auto' }} size="sm">
          <Thead background="#D9D9D9">
            <Tr borderTopLeftRadius="5px" borderTopRightRadius="5px">
              <Th>{isContract ? t('Abonnement') : t('Crédit')}</Th>
              <Th textAlign="center">{t('Qté')}</Th>
              <Th textAlign="center">{t('Prix')}</Th>
              {!discount && <Th textAlign="center"></Th>}
              {discount > 0 && <Th textAlign="center">{t('Remise')}</Th>}
              <Th textAlign="center">{t('Sous-total')}</Th>
            </Tr>
          </Thead>
          <Tbody background="#F5F5F5">
            <Tr>
              <Td>{product.type === 'giftcard' ? product.Description : product.Name}</Td>
              <Td textAlign="center">1</Td>
              <Td textAlign="center">{getProductPrice()} €</Td>
              {!discount && <Td></Td>}
              {discount > 0 && <Td textAlign="center">-{discount.toFixed(2)} €</Td>}
              <Td textAlign="center">{productAmount.toFixed(2)} €</Td>
            </Tr>
            {giftCardValue && (
              <Tr>
                <Td colSpan={4}>{t('Paiement carte cadeau')}</Td>
                <Td textAlign="center">-{(giftCardValue >= totalAmount ? totalAmount : giftCardValue).toFixed(2)} €</Td>
              </Tr>
            )}
            <Tr>
              <Td colSpan={4} fontWeight="bold" textAlign="right">
                {t('TVA incluse dans le total')}
              </Td>
              <Td textAlign="center">{vatAmount.toFixed(2)} €</Td>
            </Tr>
            <Tr borderBottomLeftRadius="5px" borderBottomRightRadius="5px" borderBottom="0px solid transparent">
              <Td colSpan={3}></Td>
              <Td fontWeight="bold" textAlign="center">
                {t('TOTAL')}
              </Td>
              <Td textAlign="center">{remainingAmount.toFixed(2)} €</Td>
            </Tr>
          </Tbody>
        </Table>
      </Stack>
      <Stack as="form" onSubmit={handleSubmit(onSubmit)} className="checkoutForm">
        {product.type === 'service' && (
          <Accordion allowToggle mb={5}>
            {/* L'encart carte cadeau n'est proposé que si la config BO l'autorise
                pour ce service. restrictToEligible=false (défaut) → toujours
                proposé ; true → uniquement pour les services éligibles. Le
                serveur revérifie au paiement (SaleService::purchaseService). */}
            {(!mondayMindbody?.giftCardConfig?.restrictToEligible ||
              mondayMindbody.giftCardConfig.eligibleServiceIds.includes(String(product.Id))) && (
              <AccordionItem className="accordionGiftCard">
                <AccordionButton>
                  <HStack w="full" justifyContent="space-between">
                    <Text>{t('Carte cadeau')}</Text>
                    <AccordionIcon />
                  </HStack>
                </AccordionButton>
                <AccordionPanel>
                  <CheckoutGiftCard />
                </AccordionPanel>
              </AccordionItem>
            )}

            <AccordionItem className="accordionPromoCode">
              <AccordionButton>
                <HStack w="full" justifyContent="space-between">
                  <Text>{t('Code Promo')}</Text>
                  <AccordionIcon />
                </HStack>
              </AccordionButton>
              <AccordionPanel>
                <CheckoutPromoCode product={product} />
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        )}

        {/* Only show payment methods if there's a remaining amount to pay */}
        {remainingAmount > 0 && (
          <>
            {!giftCardValueCoversTotalAmount && (
              <>
                {/* Paiement express : le même bloc que dans le Nouveau Panier,
                    et en tête comme là-bas. Son affichage ne dépend que des
                    deux réglages du back-office et de l'appareil, jamais du
                    panier qui l'accueille. */}
                <Stack mb={5}>
                  <WalletExpress
                    applePay={isApplePayAvailable}
                    googlePay={isGooglePayAvailable}
                    selected={
                      paymentMethod === APPLE_PAY_METHOD ? 'applepay' : paymentMethod === GOOGLE_PAY_METHOD ? 'googlepay' : null
                    }
                    onSelect={(id: WalletId) => handlePaymentChange(id === 'applepay' ? APPLE_PAY_METHOD : GOOGLE_PAY_METHOD)}
                  />
                </Stack>

                {clientHasStoredCreditCard && (
                  <Controller
                    name="StoredCreditCard"
                    control={control}
                    render={({ field: { onChange, value } }) => {
                      return (
                        <RadioGroup onChange={onChange} value={value}>
                          <Stack>
                            <Radio value="saved" isDisabled={storedCardExpiresBeforeEngagement}>
                              {t('Utiliser la carte se terminant par {{ lastFour }}', {
                                lastFour: clientCompleteInfo?.Client.ClientCreditCard?.CardNumber,
                              })}
                            </Radio>
                            <Radio value="new">{t('Utiliser un nouveau moyen de paiement')}</Radio>
                          </Stack>
                        </RadioGroup>
                      );
                    }}
                  />
                )}

                {isContract && product?.type === 'contract' && clientCreditCard?.ExpYear && clientCreditCard?.ExpMonth && (
                  <SavedCardExpirationAlert
                    card={{
                      ExpYear: clientCreditCard.ExpYear,
                      ExpMonth: clientCreditCard.ExpMonth,
                      LastFour: clientCreditCard.CardNumber,
                    }}
                    contract={product}
                  />
                )}

                <Collapse
                  in={(!isUsingSavedCreditCard || paymentMethod === 'Alma') && !WALLET_METHODS.includes(paymentMethod)}
                  unmountOnExit
                >
                  {(!isUsingSavedCreditCard || paymentMethod === 'Alma') && !WALLET_METHODS.includes(paymentMethod) && (
                    <Stack mt={5}>
                      <Heading textTransform="uppercase" size="md">
                        {t('Adresse de facturation')}
                      </Heading>
                      <FormControl isRequired={paymentMethod === 'Alma'}>
                        <FormLabel>{t('Adresse')}</FormLabel>
                        <Input {...register('BillingAddress')} />
                      </FormControl>
                      <HStack>
                        <FormControl flex={2} isRequired={paymentMethod === 'Alma'}>
                          <FormLabel>{t('Ville')}</FormLabel>
                          <Input {...register('BillingCity')} />
                        </FormControl>
                        <FormControl flex={1} isRequired={paymentMethod === 'Alma'}>
                          <FormLabel>{t('Code postal')}</FormLabel>
                          <Input {...register('BillingPostalCode')} />
                        </FormControl>
                      </HStack>

                      <Stack mt={10}>
                        <Heading textTransform="uppercase" size="md">
                          {t('Paiement')}
                        </Heading>
                        <Spacer />

                        <RadioGroup value={paymentMethod} onChange={handlePaymentChange}>
                          <Stack direction="column">
                            <HStack mb={2}>
                              <Radio value="Credit Card">Payer par carte</Radio>
                              <HStack spacing={2} ml={2}>
                                <img
                                  src="https://www.reussir-mon-ecommerce.fr/wp-content/uploads/2016/03/logo-cb-1-300x214.jpg"
                                  alt="CB"
                                  style={{ height: '30px', width: 'auto' }}
                                />
                                <img
                                  src="https://www.reussir-mon-ecommerce.fr/wp-content/uploads/2016/03/MasterCard-Logo-1-300x225.png"
                                  alt="Mastercard"
                                  style={{ height: '30px', width: 'auto' }}
                                />
                                <img
                                  src="https://www.reussir-mon-ecommerce.fr/wp-content/uploads/2016/03/logo-visa-carte-ancien-1-300x165.png"
                                  alt="Visa"
                                  style={{ height: '30px', width: 'auto' }}
                                />
                                <img
                                  src="https://www.reussir-mon-ecommerce.fr/wp-content/uploads/2016/03/logo-american-express-1-300x300.png"
                                  alt="American Express"
                                  style={{ height: '30px', width: 'auto' }}
                                />
                              </HStack>
                            </HStack>
                            {isAlmaAvailable &&
                              customPaymentMethods?.map((method: PaymentMethod) => (
                                <HStack key={method.Id} alignItems="center">
                                  {isAlmaSelectable ? (
                                    <Radio value={method.Name} isDisabled={!isAlmaSelectable}>
                                      <HStack>
                                        <span>Payer avec</span>
                                        <img
                                          src={`${import.meta.env.BASE_URL}logoalma.svg`}
                                          alt="Alma"
                                          style={{ height: '30px', width: 'auto', marginLeft: '0px' }}
                                        />
                                      </HStack>
                                    </Radio>
                                  ) : (
                                    <Tooltip label="Alma n'est pas autorisé car le total est inférieur à 100€">
                                      <Radio value={method.Name} isDisabled={!isAlmaSelectable}>
                                        <HStack>
                                          <span>Payer avec</span>
                                          <img
                                            src={`${import.meta.env.BASE_URL}logoalma.svg`}
                                            alt="Alma"
                                            style={{ height: '30px', width: 'auto', marginLeft: '0px' }}
                                          />
                                        </HStack>
                                      </Radio>
                                    </Tooltip>
                                  )}
                                </HStack>
                              ))}
                          </Stack>
                        </RadioGroup>

                        {paymentMethod === 'Alma' ? (
                          <>
                            <Stack mt={0} pl={6} borderColor="gray.200">
                              {isLoadingPaymentPlans || isRefetchingPaymentPlans ? (
                                <Stack align="center" py={4}>
                                  <Text fontSize="sm" color="gray.500">
                                    Chargement des plans de paiement...
                                  </Text>
                                </Stack>
                              ) : (updatedPaymentPlans?.length ?? 0) > 0 ? (
                                <HStack spacing={3} mb={4}>
                                  {updatedPaymentPlans
                                    ?.filter((plan) => plan.installmentsCount > 1)
                                    .map((plan, index) => (
                                      <Button
                                        key={index}
                                        size="sm"
                                        variant={selectedPlan === plan.installmentsCount ? 'solid' : 'outline'}
                                        colorScheme={selectedPlan === plan.installmentsCount ? 'gray' : 'gray'}
                                        bg={selectedPlan === plan.installmentsCount ? 'black' : 'white'}
                                        color={selectedPlan === plan.installmentsCount ? 'white' : 'black'}
                                        borderColor="gray.300"
                                        _hover={{
                                          bg: selectedPlan === plan.installmentsCount ? 'black' : 'gray.50',
                                        }}
                                        onClick={() => handlePlanSelect(plan.installmentsCount)}
                                        minW="45px"
                                        borderRadius="md"
                                      >
                                        {plan.installmentsCount}x
                                      </Button>
                                    ))}
                                </HStack>
                              ) : (
                                <Stack align="center" py={4}>
                                  <Text fontSize="sm" color="red.500">
                                    ❌ Aucun plan de paiement disponible pour ce montant
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    Veuillez choisir un autre mode de paiement
                                  </Text>
                                </Stack>
                              )}
                            </Stack>

                            {selectedPlan !== null && (updatedPaymentPlans?.length ?? 0) > 0 && (
                              <>
                                {/* DCC2 (20/11/2026) : pas de « paiement en X fois », publicité pour un crédit. */}
                                <Text pl={6}>Payer avec Alma</Text>
                                <Stack
                                  mt={4}
                                  maxW="700px"
                                  bg="#F5F5F5"
                                  p={4}
                                  borderRadius="md"
                                  className="checkoutInstallments"
                                  spacing={3}
                                >
                                  {updatedPaymentPlans
                                    ?.find((plan) => plan.installmentsCount === selectedPlan)
                                    ?.planDetails.map((detail, index) => (
                                      <HStack
                                        className="checkoutInstallment"
                                        key={detail.dueDate}
                                        justifyContent="space-between"
                                        w="full"
                                      >
                                        <Text fontSize="sm" fontWeight="medium">
                                          {index === 0
                                            ? "AUJOURD'HUI"
                                            : `LE ${new Date(detail.dueDate)
                                                .toLocaleDateString('fr-FR', {
                                                  day: 'numeric',
                                                  month: 'long',
                                                  year: 'numeric',
                                                })
                                                .toUpperCase()}`}
                                        </Text>
                                        <Text fontSize="sm" fontWeight="semibold">
                                          {Number(detail.totalAmount).toFixed(2)} €
                                        </Text>
                                      </HStack>
                                    ))}

                                  <Box borderTop="1px solid" borderColor="gray.300" pt={3} mt={3}>
                                    <HStack justifyContent="space-between" w="full">
                                      <Text fontSize="md" fontWeight="bold">
                                        Total :{' '}
                                        {(
                                          updatedPaymentPlans
                                            ?.find((plan) => plan.installmentsCount === selectedPlan)
                                            ?.planDetails.reduce(
                                              (sum, detail) => sum + Number(detail.totalAmount),
                                              0,
                                            ) || 0
                                        ).toFixed(0)}{' '}
                                        €
                                      </Text>
                                    </HStack>
                                  </Box>
                                </Stack>
                              </>
                            )}
                          </>
                        ) : null}

                        {paymentMethod !== 'Alma' ? (
                          <FormControl sx={{ mt: 10 }}>
                            <FormLabel>{t('Nom du titulaire')}</FormLabel>
                            <Input {...register('BillingName', { required: true })} />
                          </FormControl>
                        ) : null}

                        {paymentMethod === 'Credit Card' ? (
                          <>
                            <Stack direction={{ base: 'column', md: 'row' }}>
                              <FormControl isInvalid={!!errors?.CreditCardNumber}>
                                <FormLabel>{t('Numéro de carte')}</FormLabel>
                                <Input
                                  {...register('CreditCardNumber', {
                                    required: 'Numéro de carte requis',
                                    validate: (value) => {
                                      const cleanedValue = value.replace(/\s/g, '');
                                      if (isAmex(cleanedValue)) {
                                        return cleanedValue.length === 15 || 'Numéro de carte AMEX invalide';
                                      }
                                      return cleanedValue.length === 16 || 'Numéro de carte invalide';
                                    },
                                  })}
                                  as={InputMask}
                                  mask={isAmex(creditCardNumber) ? '9999 999999 99999' : '9999 9999 9999 9999'}
                                  // placeholder={isAmex(creditCardNumber) ? '1234 123456 12345' : '4242 1212 4242 1212'}
                                  maskChar={null}
                                />
                              </FormControl>

                              <HStack>
                                <FormControl isInvalid={!!errors?.Exp} minW={145}>
                                  <FormLabel>{t("Date d'expiration")}</FormLabel>
                                  <Input
                                    {...register('Exp', {
                                      required: "Date d'expiration requise",
                                      pattern: {
                                        value: /[0-1][0-9] \/ [0-9][0-9]/,
                                        message: "Date d'expiration invalide",
                                      },
                                    })}
                                    as={InputMask}
                                    // placeholder="01 / 24"
                                    mask="99 / 99"
                                    maskChar={null}
                                  />
                                  <FormErrorMessage>{errors.Exp?.message}</FormErrorMessage>
                                </FormControl>
                                <FormControl isInvalid={!!errors?.Cryptogram}>
                                  <FormLabel>{t('Cryptogramme')}</FormLabel>
                                  <Input
                                    {...register('Cryptogram', {
                                      required: 'Cryptogramme requis',
                                      pattern: {
                                        value: isAmex(creditCardNumber) ? /^[0-9]{4}$/ : /^[0-9]{3}$/,
                                        message: 'Cryptogramme invalide',
                                      },
                                    })}
                                    as={InputMask}
                                    // placeholder={isAmex(creditCardNumber) ? '1234' : '123'}
                                    mask={isAmex(creditCardNumber) ? '9999' : '999'}
                                    maskChar={null}
                                  />
                                </FormControl>
                              </HStack>
                            </Stack>
                            {isContract ? (
                              <Tooltip
                                hasArrow
                                placement="top"
                                label="L'enregistrement de votre carte est obligatoire pour activer le prélèvement automatique mensuel de votre abonnement."
                              >
                                <Box display="inline-block">
                                  <Checkbox isChecked isDisabled>
                                    {t('Enregistrer ce moyen de paiement')}
                                  </Checkbox>
                                </Box>
                              </Tooltip>
                            ) : (
                              <Checkbox {...register('SaveInfo')}>{t('Enregistrer ce moyen de paiement')}</Checkbox>
                            )}
                          </>
                        ) : null}
                      </Stack>
                    </Stack>
                  )}
                </Collapse>
              </>
            )}
          </>
        )}

        <Spacer mt={5} />

        {/* Message informatif sur SCA pour transactions nécessitant une authentification */}
        {paymentMethod === 'Credit Card' && remainingAmount >= 30 && (
          <Box
            className="scaInfoMessage"
            p={4}
            bg="blue.50"
            border="1px solid"
            borderColor="blue.200"
            borderRadius="md"
            mb={4}
            fontSize="sm"
            lineHeight="1.5"
          >
            <Text className="scaInfoText" fontWeight="medium" color="blue.800" mb={2}>
              Authentification 3D Secure {storedCreditCard === 'new' ? 'obligatoire' : 'requise'}
            </Text>
            <Text className="scaInfoText" color="blue.700">
              {storedCreditCard === 'new'
                ? "Pour votre sécurité, ce paiement nécessite une authentification 3D Secure par votre banque. Vous serez automatiquement redirigé(e) vers la page d'authentification."
                : "Pour votre sécurité, ce paiement peut nécessiter une authentification 3D Secure par votre banque. Vous serez automatiquement redirigé(e) vers la page d'authentification si nécessaire."}
            </Text>
          </Box>
        )}

        {/* Message spécial pour les produits lululemon */}
        {(Number(product.Id) === 113276 || Number(product.Id) === 113277 || Number(product.Id) === 113278) && (
          <Box
            className="lululemonMessage"
            p={4}
            bg="transparent"
            border="1px solid"
            borderColor="transparent"
            mb={4}
            fontSize="sm"
            lineHeight="1.5"
          >
            <Text className="lululemonText" textTransform="inherit">
              En achetant ce pack, vous acceptez de recevoir des e-mails promotionnels de la part de notre partenaire
              lululemon sur les produits, services, offres, histoires et événements conformément à la{' '}
              <Text
                className="lululemonLink"
                as="a"
                href="https://www.lululemon.fr/fr-fr/legal/politique-de-confidentialite/privacy.html"
                target="_blank"
                rel="noopener noreferrer"
                color="blue.500"
                textDecoration="underline"
                _hover={{ textDecoration: 'none' }}
              >
                politique de confidentialité
              </Text>{' '}
              de lululemon (vous pouvez vous désabonner via lululemon à tout moment).
            </Text>
          </Box>
        )}

        {isContract && (
          <ContractAgreementCheckbox
            pdfUrl={contractAgreement?.pdfUrl}
            agreementTerms={contractAgreement?.agreementTerms}
          />
        )}

        <Tooltip label={getDisabledTooltipMessage()} isDisabled={!getDisabledTooltipMessage()}>
          <Button
            ml="auto"
            type="submit"
            isLoading={paymentInProgress || isSubmitting || almaRedirectInProgress}
            isDisabled={
              (remainingAmount === 0 ? false : !submitEnabled || disableConfirmAlmaPayment) ||
              isSubmitting ||
              paymentInProgress ||
              almaRedirectInProgress
            }
          >
            {isSubmitting || almaRedirectInProgress
              ? paymentMethod === 'Alma'
                ? almaRedirectInProgress
                  ? 'Redirection vers Alma...'
                  : 'Préparation du paiement...'
                : WALLET_METHODS.includes(paymentMethod)
                  ? 'Ouverture du paiement sécurisé...'
                  : isContract
                    ? "Souscription en cours..."
                    : 'Traitement du paiement...'
              : remainingAmount === 0
              ? t('Confirmer')
              : paymentMethod === 'Alma'
              ? t('Commencer le paiement')
              : WALLET_METHODS.includes(paymentMethod)
              ? `Payer avec ${paymentMethod}`
              : isContract
                ? "Souscrire à l'abonnement"
                : t('Confirmer le paiement')}
          </Button>
        </Tooltip>

        {/* Départ vers la page de paiement du portefeuille : le client ne doit
            ni cliquer ailleurs ni croire que rien ne se passe. */}
        <WalletPaymentOverlay isOpen={walletStep !== 'idle'} currentStep={walletStep} walletName={paymentMethod} />
        <VStack spacing={1} alignItems="flex-end">
          <Text fontSize="xs" textAlign="right">
            {isContract
              ? "Votre abonnement peut apparaître dans Mon Compte d'ici 1 minute."
              : "Le crédit acheté peut mettre quelques instants avant de s'afficher dans votre compte."}
          </Text>
          <Collapse in={isSubmitting || almaRedirectInProgress}>
            <Text fontSize="sm" color="gray.600" fontStyle="italic">
              {paymentMethod === 'Alma'
                ? almaRedirectInProgress
                  ? 'Redirection en cours vers Alma, veuillez ne pas actualiser la page...'
                  : 'Veuillez patienter pendant que nous préparons votre paiement Alma...'
                : 'Votre paiement est en cours de traitement, merci de ne pas actualiser la page...'}
            </Text>
          </Collapse>
          <Collapse in={showFallbackRedirect && paymentMethod === 'Credit Card'}>
            <Box mt={3} p={3} bg="orange.50" borderLeft="4px solid" borderLeftColor="orange.400" borderRadius="md">
              <Text fontSize="sm" color="orange.700" fontWeight="medium">
                ⚠️ Si la redirection ne se fait pas automatiquement,{' '}
                <Button
                  variant="link"
                  size="sm"
                  color="orange.600"
                  textDecoration="underline"
                  fontWeight="bold"
                  onClick={handleFallbackRedirect}
                  p={0}
                  h="auto"
                  minW="auto"
                  _hover={{ color: 'orange.800' }}
                >
                  cliquez ici pour accéder à votre compte
                </Button>
              </Text>
            </Box>
          </Collapse>
        </VStack>
      </Stack>
    </FormProvider>
  );
}
